/**
 * SegmentedTab — one pill of a segmented toggle (the active-wash lane
 * treatment). Promoted to ui/ from the Surface's local SurfaceTabButton
 * (#542 slice-2 review): the same pill was being re-implemented per surface
 * (Surface tabs, the compose modal's mode/side toggles), so a design tweak to
 * the active treatment would drift. Wrap a row of these in a
 * `role="tablist"` container (the caller owns the border/rounding wrapper).
 *
 * `count` is optional — the Surface tabs carry one, plain toggles don't.
 */

import { cn } from "@/lib/utils";

export function SegmentedTab({
  active,
  onClick,
  label,
  count,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={cn(
        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors",
        active
          ? // text-foreground (not the candidate-swapped plane ink): the wash is
            // light in EVERY ground candidate, so the label needs wash-safe ink.
            "bg-active-wash text-foreground ring-1 ring-inset ring-active-edge/50"
          : "text-plane-muted hover:text-plane-foreground",
      )}
    >
      <span>{label}</span>
      {count !== undefined ? (
        <span
          data-testid={`${testId}-count`}
          className={cn(
            "rounded-full px-1.5 text-[10px] font-semibold leading-none",
            active ? "bg-active-edge/15 text-active-fg" : "bg-plane-muted/15",
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}
